import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { hasAtLeast, profileConfirmed, grantsOf, topRank, ROLE_LABELS, ROLE_ORDER, ROLE_RANK } from "../auth/roles";
import Layout from "./Layout";
import { formatWhen } from "./Shared";
import { useDocumentWord } from "../documentWord";

// ── What is project-portal's own ─────────────────────────────────────────────
//
// Shared.jsx, Layout.jsx, AccessibilityPanel.jsx, a11y/A11yContext.jsx and
// index.css are copies of boku-ui's, kept identical so `diff` against boku-ui
// shows only the additions listed in
// knowledge/tasks/completed/2026-10-22-project-portals-shared-components-match-boku-ui.md
// (2026-10-30). Whatever only this portal needs lives here instead: the shell
// that fills boku-ui's Layout (PortalLayout), people as `Name <email>`, the
// session-limit notice, and the time-zone grouping.

// ── The shell ────────────────────────────────────────────────────────────────

// What the banner shows before the first answer, or under a test's useAuth.
const NO_DUTIES = { blocking: 0, total: 0, signatures: 0, waiting: 0 };

// The portal's own rules for its top bar, beside boku-ui's globalStyles (which
// Layout injects). No backticks in here: this is a template literal.
export const portalStyles = `
  /* The signed-in person's name, in the top bar. A flat 160px cut real names
     off mid-word; clamp(160px, 20vw, 340px) still did, because 20vw is measured
     on the viewport while the bar is capped at 1100px (raised 2026-09-07). Real
     names here run past 40 characters. The name is the only elastic thing in
     the row, so it is what gives; the ellipsis is the last resort, and the
     title carries the full name. In the open phone menu boku-ui's rule lets it
     wrap instead. */
  [data-user-label] {
    max-width: clamp(200px, 30vw, 460px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 0 1 auto;
    min-width: 0;
  }
  /* Short, fixed labels: they have nothing to gain from shrinking, and their
     wrapping is what made the row look cramped. */
  [data-topbar-actions] button { flex-shrink: 0; white-space: nowrap; }

  /* The account in the open phone menu (2026-09-30): the bar has no room for
     the name below 768px, and the installed app has no URL bar to type
     /profile into, so the menu says who is signed in, their roles, and reaches
     Profile and Sign out. The e-mail, roles and Profile rows are data-menu-only:
     on a desktop the name link and Sign out are all the bar shows. */
  @media (max-width: 768px) {
    [data-nav-right] [data-nav-account-email],
    [data-nav-right] [data-nav-account-roles] {
      padding: 0 14px;
      overflow-wrap: anywhere;
    }
    [data-nav-right] [data-nav-account-email] { font-size: 13px; }
    [data-nav-right] [data-nav-account-roles] { font-size: 12px; padding-bottom: 4px; }
    /* The name's underline marks it as a link in the bar. In the menu it is a
       full-width row with rounded corners, where the same border drew a curved
       white line under the row (seen in the harness, 2026-10-30). */
    [data-nav-right] [data-nav-account-name] { border-bottom: none !important; }
  }
`;

const barButton = {
  background: "rgba(0,0,0,0.18)", border: "none", color: "#fff", fontSize: 13, fontWeight: 500,
  padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
};

/**
 * project-portal's page shell: boku-ui's `Layout` with the portal's navigation
 * as `nav` and the signed-in account as `right`, and the duties banner as the
 * first thing in the page. Pages render their own h1, so no `title`.
 */
export function PortalLayout({ children }) {
  const { user, logout, duties = NO_DUTIES, refreshDuties } = useAuth();
  // The banner counts every document waiting, so it says the CURRENT word.
  const word = useDocumentWord();
  const navigate = useNavigate();
  const location = useLocation();
  // Problems that are true right now — not warnings that once happened. The
  // badge has to clear itself when things are fixed, or "everything is fine"
  // becomes indistinguishable from "nobody cleared the marker".
  const [problems, setProblems] = useState(0);
  // An account the facility has not confirmed yet (2026-09-28) is refused
  // everything but its own profile, so neither background read below is asked
  // for it — each would only be a 403 swallowed by `.catch`, once a minute.
  const confirmed = profileConfirmed(user);
  useEffect(() => {
    if (!user || !confirmed) return;
    const load = () => api.get("/api/health/problems")
      .then(r => setProblems(r.count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user, confirmed]);

  // Documents this person still has to confirm. Until this existed the first a
  // researcher heard of a mandatory policy was the upload gate refusing them —
  // after they had selected their files.
  //
  // The counts live in AuthContext (DUTIES, 2026-10-06): as a state of the
  // shell, re-read on navigation only, confirming a document on a card left
  // this banner asking for it until a reload. Each confirm and approve
  // refreshes the shared copy itself; navigation refreshes it here, and not on
  // the minute timer — unlike the health poll it is not watching for something
  // going wrong.
  useEffect(() => { refreshDuties?.(); }, [refreshDuties, location.pathname]);

  // `replace`: Back from the sign-in page must not land on a page of the
  // account that just left, and `signed_out=1` tells that page to forward
  // nobody even if a user reappears (SIGNOUT, 2026-09-29).
  const handleLogout = () => { logout(); navigate("/login?signed_out=1", { replace: true }); };

  // Which roles the account holds, strongest first and each named once — a
  // person with operator in two groups is an operator, not "Operator ·
  // Operator". Falls back to the strongest alone (ROLE_ORDER is indexed by
  // rank) if the grants name none the labels know.
  const roleNames = [...new Set(grantsOf(user).map(g => g.role))]
    .filter(r => ROLE_LABELS[r])
    .sort((a, b) => ROLE_RANK[a] - ROLE_RANK[b])
    .map(r => ROLE_LABELS[r]);
  const strongest = topRank(user);
  const rolesLine = roleNames.length
    ? roleNames.join(" · ")
    : (strongest !== null ? ROLE_LABELS[ROLE_ORDER[strongest]] || "" : "");

  const navLink = (path, label, badge = 0, badgeLabel = null) => {
    const active = location.pathname.startsWith(path);
    return (
      <Link
        to={path}
        // Marks the current page for anyone not seeing the highlight; the
        // background tint alone conveys nothing to a screen reader.
        aria-current={active ? "page" : undefined}
        style={{
          // Full white on the dark accent, 5.48:1; the active pill 7.87:1.
          color: "#ffffff",
          textDecoration: "none", fontSize: 14,
          fontWeight: active ? 700 : 500,
          padding: "5px 13px", borderRadius: 6,
          background: active ? "rgba(0,0,0,0.22)" : "transparent",
          transition: "all 0.15s",
        }}
      >
        {label}
        {badge > 0 && (
          <span
            aria-label={badgeLabel
              ? badgeLabel(badge)
              : `${badge} active problem${badge === 1 ? "" : "s"}`}
            style={{
              marginLeft: 6, background: "#b91c1c", color: "#fff", borderRadius: 9,
              padding: "0 6px", fontSize: 11, fontWeight: 700, lineHeight: "16px",
              display: "inline-block", minWidth: 16, textAlign: "center",
            }}
          >{badge}</span>
        )}
      </Link>
    );
  };

  const nav = (
    <nav id="primary-nav" style={{ display: "flex", gap: 4 }}>
      {/* The badge still counts only BLOCKING documents — a red number on
          optional reading is how a badge gets ignored. Everything else that
          is waiting is said in words, in the banner at the head of the page. */}
      {navLink("/projects", "Projects", duties.blocking,
               n => `${n} document${n === 1 ? "" : "s"} to confirm before you can send data`)}
      {navLink("/jobs", "Jobs")}
      {/* Anybody with a group has figures worth seeing; the server narrows
          them to what that person may be told about. */}
      {hasAtLeast(user, "operator") && navLink("/stats", "Statistics")}
      {/* The page behind this holds Settings, Users, Groups and
          Notifications — "Users" named only one of the four. */}
      {hasAtLeast(user, "operator") && navLink("/admin/users", "Management")}
      {hasAtLeast(user, "operator") && navLink("/admin/log", "Log", problems)}
    </nav>
  );

  const name = user?.full_name || user?.email;
  const right = user ? (
    <div data-nav-account style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      {/* The name is where people already look for "my account", so it is the
          way into the profile rather than another nav item. The width caps
          live in portalStyles: an inline style cannot carry a media query. */}
      <Link
        to="/profile"
        data-user-label
        data-nav-account-name
        title={`Personal Administration — ${name || ""}`}
        style={{ color: "#ffffff", fontSize: 13, textDecoration: "none", borderBottom: "1px solid rgba(255,255,255,0.5)" }}
      >
        {name}
      </Link>
      {user.full_name && <span data-menu-only data-nav-account-email>{user.email}</span>}
      {rolesLine && <span data-menu-only data-nav-account-roles>{rolesLine}</span>}
      <Link
        to="/profile"
        data-menu-only
        aria-current={location.pathname.startsWith("/profile") ? "page" : undefined}
        style={{ color: "#ffffff", textDecoration: "none", fontWeight: 600, background: "rgba(0,0,0,0.22)" }}
      >
        Profile · Personal Administration
      </Link>
      <button type="button" onClick={handleLogout} style={barButton}>Sign out</button>
    </div>
  ) : null;

  return (
    <Layout nav={nav} right={right} home="/projects" statementPath="/accessibility">
      <style>{portalStyles}</style>
      {/* Something is waiting on this person, and nothing used to say so on the
          page they land on — only a small red number on one nav item, counting
          blocking documents alone. A quotation waiting for their signature was
          not mentioned anywhere at all.

          role="status", not "alert": nothing has gone wrong, and a page that
          shouts on every load gets dismissed on every load. #6b4a00 on the
          amber tint over the page's #f5f5f5 is 6.86:1. */}
      {user && duties.waiting > 0 && (
        <div role="status" data-duties-banner style={{
          margin: "0 0 20px", padding: "12px 16px", borderRadius: 10,
          background: "rgba(230,159,0,0.10)", border: "1px solid rgba(230,159,0,0.45)",
          color: "#6b4a00", fontSize: 14, display: "flex", gap: 12,
          alignItems: "center", flexWrap: "wrap",
        }}>
          <span aria-hidden="true" style={{ fontSize: 18 }}>✍️</span>
          <span>
            {[
              duties.signatures > 0
                && `${duties.signatures} ${word({ plural: duties.signatures !== 1 })} to sign`,
              duties.total > 0
                && `${duties.total} document${duties.total === 1 ? "" : "s"} to confirm`,
            ].filter(Boolean).join(" · ")}
            {/* Named rather than only coloured — colour cannot be the sole
                carrier of meaning, and it is the blocking ones that stop work. */}
            {duties.blocking > 0 && (
              <strong> — {duties.blocking} of them {duties.blocking === 1 ? "blocks" : "block"} sending data.</strong>
            )}
          </span>
          <span style={{ flex: 1 }} />
          <Link to="/profile" style={{ color: "#6b4a00", fontWeight: 600 }}>
            Personal Administration
          </Link>
        </div>
      )}
      {children}
    </Layout>
  );
}

// ── People ───────────────────────────────────────────────────────────────────

// A person who wrote, approved, asked for or must sign something: `Name <email>`,
// or the address alone when there is no name (or the name IS the address).
// Never the name alone — two accounts may share one, and "approved by Anna
// Huber" under a version Anna Huber composed reads as one person approving their
// own (PERSON, 2026-10-06). The API sends people as `{name, email}`; an e-mail is
// long and unbreakable, hence `data-break`.
export function personText(person) {
  if (!person) return "";
  if (typeof person === "string") return person;
  const { name, email } = person;
  if (name && email && name !== email) return `${name} <${email}>`;
  return email || name || "";
}

export function PersonLabel({ name, email }) {
  const text = personText({ name, email });
  return text ? <span data-break>{text}</span> : null;
}

// ── Time zones ───────────────────────────────────────────────────────────────

// The zones anyone here will realistically pick, offered before the other ~590.
// Presentation only — the backend validates against the tz database and stays
// the authority on what is a valid zone.
export const COMMON_TIMEZONES = ["UTC", "Europe/Vienna", "Europe/Prague", "Europe/Berlin", "Europe/London"];

/**
 * Turns the backend's flat zone list into <optgroup>s: a "Common" group first,
 * then one group per IANA region (Africa, America, Asia, Europe, …).
 *
 * A flat alphabetical list of ~600 entries is a poor control on a desktop and
 * worse on a phone. Grouping keeps it a native <select> — so the iOS and
 * Android pickers, keyboard type-ahead and screen readers all keep working,
 * which a custom combobox would have put at risk.
 */
export function groupTimezones(zones) {
  if (!Array.isArray(zones) || zones.length === 0) return [];
  const common = COMMON_TIMEZONES.filter(z => zones.includes(z));
  const groups = new Map();
  for (const zone of zones) {
    if (common.includes(zone)) continue;
    // "Europe/Vienna" → Europe; bare names like "UTC" or "CET" have no region.
    const region = zone.includes("/") ? zone.split("/")[0] : "Other";
    if (!groups.has(region)) groups.set(region, []);
    groups.get(region).push(zone);
  }
  return [
    ...(common.length ? [{ group: "Common", options: common }] : []),
    ...[...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, options]) => ({ group, options })),
  ];
}

// ── The sign-in session limit, stated where a transfer is started ────────────
//
// A transfer runs inside the sign-in session and is abandoned if that session
// ends first (JOB-529B91: 8 hours in, 37.9% of 883 GiB, nothing kept). The
// limit existed only in the manual, so nobody deciding whether to start a
// 300 GB upload had it in front of them.
//
// One component for both tabs so the two wordings cannot drift.
//
// **It states one thing: the deadline.** Earlier versions also quoted the
// session's *length* in hours, which turned out to be both useless and actively
// confusing:
//
//   - Useless, because nothing about "96 hours" helps you decide whether an
//     upload fits. What decides that is how much time is left, right now.
//   - Confusing, because the length of the session you hold and the length a
//     new sign-in would get are different numbers whenever an admin has changed
//     the setting — a JWT's expiry is inside its signed payload and cannot be
//     revised after issue. Showing both produced "96 hours … New sessions last
//     720 hours", two numbers in one box, which reads as a contradiction. That
//     the setting only applies at the next sign-in is explained where it is
//     actionable: on the Settings page, next to the field.
//
// So: the moment it ends, and how long that is from now. Nothing else.
//
// Deliberately not role="alert": this is standing information, not an error,
// and an alert would be announced afresh on every tab switch.

// Which calendar day an instant falls on, in a named zone. Shared by anything
// that has to say "today" about a time it renders somewhere else.
function dayIn(date, zone) {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: zone || undefined, year: "numeric", month: "2-digit", day: "2-digit",
    }).format(date);
  } catch {
    // An unloadable zone falls back to the viewer's own, exactly as formatWhen
    // does — the two must agree or the comparison is meaningless again.
    return date.toDateString();
  }
}

export function SessionLimitNotice({ hours, sessionHours = null, expiresAt, zone, kind = "upload" }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return undefined;
    // A tab left open for hours would otherwise keep showing the figure from
    // when it was opened. Once a minute matches the problem poll's cadence.
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, [expiresAt]);

  // With no deadline there is nothing useful to say, so say nothing rather than
  // a vague warning. (`hours`/`sessionHours` are still accepted so an older
  // caller does not break; they are no longer displayed.)
  if (!expiresAt) return null;

  const left = expiresAt.getTime() - now;
  const expired = left <= 0;
  const h = Math.floor(left / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const remaining = h > 0 ? `${h}h ${m}m` : `${m}m`;

  // Include the date whenever the deadline is not today. At the old 8-hour
  // default it almost always was; at 96 hours "ends at 11:44" is four days
  // away and a bare clock time is genuinely misleading.
  //
  // "Today" has to be decided in the SAME zone the time is rendered in.
  // `toDateString()` answers in the viewer's local zone while `formatWhen`
  // renders in the configured one, so between local midnight and UTC midnight
  // the two disagree and the date appears on a deadline that is, as displayed,
  // later today. Harmless-looking, and it made the suite fail only when it was
  // run in the evening — which is the worst kind of test to inherit.
  const sameDay = dayIn(new Date(), zone) === dayIn(expiresAt, zone);
  const when = formatWhen(expiresAt, { zone, withDate: !sameDay });

  return (
    <div style={{
      marginBottom: 16, padding: "10px 14px", borderRadius: 8,
      background: expired ? "rgba(185,28,28,0.06)" : "rgba(0,150,64,0.06)",
      border: `1px solid ${expired ? "rgba(185,28,28,0.35)" : "rgba(0,150,64,0.25)"}`,
      color: expired ? "#b91c1c" : "#00551f",
      fontSize: 13, display: "flex", gap: 10, alignItems: "flex-start",
    }}>
      <span aria-hidden="true" style={{ fontSize: 15, lineHeight: "18px" }}>⏱</span>
      <div>
        {expired ? (
          <>Your sign-in session has expired — sign out and in again before starting
            {kind === "download" ? " a download" : " an upload"}.</>
        ) : (
          <>
            {kind === "download" ? "A download" : "An upload"} must finish before your sign-in
            session ends, in <strong>{remaining}</strong> ({when}).
            {kind === "download"
              ? " In Chrome and Edge it runs inside this page and stops when the session ends; Firefox, Safari and the wget command carry on regardless."
              : " If it ends first the upload is abandoned and nothing partial is kept."}
          </>
        )}
        {!expired && (
          <div style={{ marginTop: 3, color: "#0b6b33" }}>
            {/* A rate, not a total: any total is only true at one session
                length, whereas GB-per-hour holds at every length. From the
                12.5 MB/s measured on JOB-529B91. */}
            Roughly 45 GB per hour on a 100 Mbit/s connection.
          </div>
        )}
      </div>
    </div>
  );
}
